import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Dumbbell, TrendingUp, Award, Calendar, Play, FileText, Utensils, BookOpen, Users, Trophy, Lock, CheckCircle, ChevronRight, Sparkles, MessageCircle, ExternalLink, Download, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({ total_workouts: 0, total_exercises: 0, current_streak: 0 });
  const [resources, setResources] = useState({ ebooks: [], conversatorios: [], retos: [] });
  const [premiumData, setPremiumData] = useState(null);
  const [purchasedProgramsData, setPurchasedProgramsData] = useState([]);
  const [trainingPlan, setTrainingPlan] = useState({ routine: null, weeks_active: 0, show_intensity_banner: false, show_new_cycle_banner: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) return;

    try {
      const [userRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/workouts/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setUser(userRes.data);
      setStats(statsRes.data);

      // Obtener recursos dinámicos según el nivel del usuario
      const userLevel = userRes.data?.subscription_level || 'free';
      const levelMapping = { 
        principiante: 'nivel_1', 
        intermedio: 'nivel_2', 
        avanzado: 'nivel_3', 
        premium: 'premium',
        free: 'free'
      };
      const contentLevel = levelMapping[userLevel] || 'free';

      const [ebooksRes, conversatoriosRes, retosRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/content?category=ebook`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/content?category=mindset`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/challenges`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);

      // Filtrar por nivel de acceso del usuario
      const filterByLevel = (items) => {
        return items.filter(item => {
          const itemLevels = item.levels || [item.level];
          if (itemLevels.includes('free')) return true;
          if ((item.program_ids || []).length > 0) return true; // el backend ya filtro por programa
          if (userLevel === 'premium') return true;
          if (itemLevels.includes(contentLevel)) return true;
          const levelHierarchy = ['free', 'nivel_1', 'nivel_2', 'nivel_3', 'premium'];
          const userLevelIdx = levelHierarchy.indexOf(contentLevel);
          return itemLevels.some(lvl => levelHierarchy.indexOf(lvl) <= userLevelIdx);
        });
      };

      setResources({
        ebooks: filterByLevel(ebooksRes.data || []),
        conversatorios: filterByLevel(conversatoriosRes.data || []),
        retos: retosRes.data || []
      });

      // Si el usuario es Premium, cargar datos del programa (Telegram, Material Extra) y su rutina personal
      if (userLevel === 'premium') {
        try {
          const programsRes = await axios.get(`${BACKEND_URL}/api/programs`);
          // Club VIP: tomar los datos del programa Club VIP por ID; si no existe, comportamiento anterior
          const premiumProgram = programsRes.data.find(p => p.program_id === 'prog_nivel_3_001') || programsRes.data.find(p => 
            p.level === 'premium' || 
            p.name?.toLowerCase().includes('premium') ||
            p.id === 'premium'
          );
          if (premiumProgram) {
            setPremiumData({
              telegram_link: premiumProgram.telegram_link || '',
              whatsapp_link: premiumProgram.whatsapp_link || '',
              extra_materials: premiumProgram.extra_materials || []
            });
          }
        } catch (err) {
          console.error('Error fetching premium data:', err);
        }
        
        // Cargar la rutina personalizada del usuario
        try {
          const planRes = await axios.get(`${BACKEND_URL}/api/workout-routine`, { headers: { Authorization: `Bearer ${token}` } });
          setTrainingPlan(planRes.data);
        } catch (err) {
          // sin rutina
        }
      }

      // Cargar la información de TODOS los programas comprados (para mostrar sus links de WhatsApp)
      const purchasedProgramIds = userRes.data?.purchased_programs || [];
      if (purchasedProgramIds.length > 0) {
        try {
          const programsRes = await axios.get(`${BACKEND_URL}/api/programs`);
          const owned = (programsRes.data || []).filter(p => 
            purchasedProgramIds.includes(p.program_id) || purchasedProgramIds.includes(p.id)
          );
          setPurchasedProgramsData(owned);
        } catch (err) {
          console.error('Error fetching purchased programs:', err);
        }
      }

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Definir contenido por nivel
  const getProgramContent = (level) => {
    const content = {
      free: {
        name: 'Gratuito',
        color: 'text-zinc-400',
        bgColor: 'bg-zinc-800/50',
        training: { days: 0, locked: true },
        nutrition: { type: 'none', locked: true },
        features: [
          { name: 'Contenido gratuito limitado', available: true },
          { name: 'Videos de entrenamiento', available: false },
          { name: 'Plan nutricional', available: false },
          { name: 'Tracker de progresión', available: false },
        ]
      },
      principiante: {
        name: 'Nivel 1 - Principiante',
        color: 'text-blue-400',
        bgColor: 'bg-blue-500/20',
        training: { days: 4, locked: false },
        nutrition: { type: 'Formulario personalizado', locked: false },
        features: [
          { name: '4 días de entrenamiento', available: true },
          { name: 'Plan nutricional (formulario)', available: true },
          { name: 'Biblioteca educativa básica', available: true },
          { name: 'Tracker de progresión', available: false },
          { name: 'Challenges (pago adicional)', available: false },
        ]
      },
      intermedio: {
        name: 'Nivel 2 - Intermedio',
        color: 'text-purple-400',
        bgColor: 'bg-purple-500/20',
        training: { days: 5, locked: false },
        nutrition: { type: '5 menús diferentes', locked: false },
        features: [
          { name: '5 días de entrenamiento', available: true },
          { name: '5 opciones de menú', available: true },
          { name: 'Biblioteca educativa ampliada', available: true },
          { name: 'Ebook de nutrición y entreno', available: true },
          { name: 'Tracker de progresión', available: true },
        ]
      },
      avanzado: {
        name: 'Nivel 3 - Avanzado',
        color: 'text-orange-400',
        bgColor: 'bg-orange-500/20',
        training: { days: 6, locked: false },
        nutrition: { type: '6 menús especializados', locked: false },
        features: [
          { name: '6 días de entrenamiento', available: true },
          { name: '3 menús masa muscular + 3 reducción grasa', available: true },
          { name: 'Biblioteca educativa completa', available: true },
          { name: 'Todos los ebooks incluidos', available: true },
          { name: 'Conversatorios exclusivos', available: true },
          { name: 'Retos gratuitos seleccionados', available: true },
          { name: 'Tracker de progresión', available: true },
        ]
      },
      premium: {
        name: 'Premium - Acceso Total',
        color: 'text-pink-400',
        bgColor: 'bg-pink-500/20',
        training: { days: 6, locked: false },
        nutrition: { type: 'Todos los planes disponibles', locked: false },
        features: [
          { name: 'Plan de entrenamiento por 6 días', available: true },
          { name: '3 menús para aumento de masa muscular', available: true },
          { name: '3 menús para reducir porcentaje de grasa', available: true },
          { name: 'Todos nuestros e-books', available: true },
          { name: 'Acceso a todo el contenido de Academia', available: true },
          { name: 'Acceso a todos los podcast de la sección mindset', available: true },
          { name: 'Acceso a todos los challenges', available: true },
          { name: 'Tracker de progresión', available: true },
          { name: '15% off en nuestro coaching online con la coach que elijas', available: true },
        ]
      }
    };
    return content[level] || content.free;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  const programContent = getProgramContent(user?.subscription_level);

  // Club VIP: el acceso al bloque (comunidad y material extra) depende del PROGRAMA Club VIP, no solo del nivel.
  // Usuarias antiguas sin programas asignados mantienen el comportamiento por nivel Premium.
  const assignedPrograms = user?.purchased_programs || [];
  const hasClubVipAccess = user?.role === 'admin' || (
    user?.subscription_level === 'premium' && (
      assignedPrograms.length === 0 ||
      assignedPrograms.includes('prog_nivel_3_001') ||
      assignedPrograms.includes('premium')
    )
  );

  // Grupos de comunidad (WhatsApp / Telegram) de cada programa comprado. El Club VIP ya tiene su propio bloque.
  const programCommunityLinks = purchasedProgramsData
    .filter(p => !(hasClubVipAccess && p.program_id === 'prog_nivel_3_001'))
    .flatMap(p => [
      p.whatsapp_link ? { key: `${p.program_id || p.id}-whatsapp`, prog: p, kind: 'whatsapp', href: p.whatsapp_link } : null,
      p.telegram_link ? { key: `${p.program_id || p.id}-telegram`, prog: p, kind: 'telegram', href: p.telegram_link } : null,
    ])
    .filter(Boolean);

  // Material Extra de cada programa comprado (el del Club VIP ya se muestra en su propio bloque).
  const programExtraMaterials = purchasedProgramsData
    .filter(p => !(hasClubVipAccess && p.program_id === 'prog_nivel_3_001'))
    .flatMap(p => (p.extra_materials || []).map((material, idx) => ({ key: `${p.program_id || p.id}-extra-${idx}`, prog: p, material })));

  return (
    <div className="min-h-screen bg-[#09090b] py-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-barlow font-bold text-white" data-testid="dashboard-title">
            ¡Hola, <span className="text-pink-500">{user?.name}</span>!
          </h1>
          <div className="flex items-center gap-3 mt-3">
            <span className={`px-4 py-1.5 rounded-full text-sm font-bold border ${programContent.bgColor} ${programContent.color} border-current/30`}>
              {programContent.name}
            </span>
            {user?.subscription_expires_at && user?.subscription_level !== 'free' && (
              <span className="text-sm text-zinc-500">
                Expira: {new Date(user.subscription_expires_at).toLocaleDateString('es-ES')}
              </span>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 hover:border-pink-500/30 transition-all">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-pink-500/20 rounded-xl flex items-center justify-center">
                <Dumbbell className="w-6 h-6 text-pink-500" />
              </div>
              <div>
                <p className="text-3xl font-barlow font-bold text-white">{stats.total_workouts}</p>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Entrenamientos</p>
              </div>
            </div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 hover:border-blue-500/30 transition-all">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-3xl font-barlow font-bold text-white">{stats.total_exercises}</p>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Ejercicios</p>
              </div>
            </div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 hover:border-orange-500/30 transition-all">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <p className="text-3xl font-barlow font-bold text-white">{stats.current_streak || 0}</p>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Días racha</p>
              </div>
            </div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 hover:border-pink-500/30 transition-all">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-pink-500/20 rounded-xl flex items-center justify-center">
                <Award className="w-6 h-6 text-pink-500" />
              </div>
              <div>
                <p className="text-3xl font-barlow font-bold text-white">{programContent.training.days}</p>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Días entreno</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Columna Principal - Acciones Rápidas */}
          <div className="lg:col-span-2 space-y-6">
            {/* Mi Plan de Entrenamiento */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-3xl p-6 hover:border-pink-500/30 transition-all">
              <h2 className="text-xl font-barlow font-bold text-white mb-5 flex items-center gap-3">
                <div className="w-10 h-10 bg-pink-500/20 rounded-xl flex items-center justify-center">
                  <Play className="w-5 h-5 text-pink-500" />
                </div>
                Mi Plan de Entrenamiento
              </h2>
              
              {user?.subscription_level === 'free' ? (
                <div className="text-center py-10 border border-dashed border-zinc-700 rounded-2xl">
                  <Lock className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400 mb-4">No tienes un programa activo</p>
                  <Link to="/programs">
                    <Button className="bg-pink-600 hover:bg-pink-500 text-white rounded-full px-6">
                      Ver Club VIP
                    </Button>
                  </Link>
                </div>
              ) : !trainingPlan.routine ? (
                <div className="text-center py-10 border border-dashed border-zinc-700 rounded-2xl px-6" data-testid="dashboard-no-routine">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-pink-500/10 border border-pink-500/20 flex items-center justify-center">
                    <FileText className="w-7 h-7 text-pink-400" />
                  </div>
                  <p className="text-white font-bold mb-2">Recuerda llenar el formulario</p>
                  <p className="text-zinc-400 text-sm mb-5 max-w-md mx-auto">Completa el formulario para obtener tu rutina de entrenamiento personalizada.</p>
                  <Link to="/training-plan/form">
                    <Button className="bg-pink-600 hover:bg-pink-500 text-white rounded-full px-6" data-testid="dashboard-fill-form-btn">
                      Llenar formulario
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3" data-testid="dashboard-routine-days">
                  {trainingPlan.routine.days?.map(day => (
                    <Link key={day.day_number} to={`/training-plan/day/${day.day_number}`} className="block group">
                      <div className="flex items-center justify-between p-4 bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/50 hover:border-pink-500/50 rounded-xl transition-all">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-pink-500 text-white rounded-xl flex items-center justify-center font-barlow font-bold text-lg">
                            {day.day_number}
                          </div>
                          <div>
                            <span className="font-medium text-white">Día {day.day_number}</span>
                            <p className="text-sm text-zinc-500">{day.focus}</p>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-pink-500 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </Link>
                  ))}
                  <Link to="/training-plan" className="block mt-4 text-center text-sm text-pink-400 hover:text-pink-300 underline underline-offset-4">
                    Ver mi plan completo
                  </Link>
                </div>
              )}
            </div>

            {/* Plan Nutricional */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-3xl p-6 hover:border-green-500/30 transition-all">
              <h2 className="text-xl font-barlow font-bold text-white mb-5 flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
                  <Utensils className="w-5 h-5 text-green-500" />
                </div>
                Plan Nutricional
              </h2>
              
              {user?.subscription_level === 'free' ? (
                <div className="text-center py-10 border border-dashed border-zinc-700 rounded-2xl">
                  <Lock className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400 text-sm">Desbloquea con tu suscripción</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-5 rounded-xl bg-zinc-800/50 border border-zinc-700/50">
                    <p className="font-bold text-green-400 text-lg">Mi plan nutricional</p>
                    <p className="text-sm text-zinc-400 mt-2">Este plan está diseñado basado en la información diligenciada en el formulario de nutrición, así que asegúrate de hacerlo muy específico y basado en tu objetivo actual.</p>
                  </div>
                  <Link to={
                    user?.subscription_level === 'intermedio' ? '/nutrition-form-level2' : 
                    (user?.subscription_level === 'premium' || user?.subscription_level === 'avanzado') ? '/nutrition-form-premium' : 
                    '/nutrition-form'
                  }>
                    <Button className="w-full bg-green-600 hover:bg-green-500 text-white font-bold rounded-full py-6">
                      Acceder a Mi Plan
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            {/* Acciones Rápidas */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-3xl p-6">
              <h2 className="text-xl font-barlow font-bold text-white mb-5">Acciones Rápidas</h2>
              <div className="grid grid-cols-2 gap-4">
                <Link to="/tracker">
                  <Button className="w-full h-auto py-5 bg-pink-600 hover:bg-pink-500 text-white rounded-2xl flex flex-col items-center gap-2 transition-all hover:scale-[1.02]">
                    <Dumbbell className="w-7 h-7" />
                    <span className="text-sm font-medium">Registrar Entreno</span>
                  </Button>
                </Link>
                <Link to="/academia">
                  <Button className="w-full h-auto py-5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-2xl flex flex-col items-center gap-2 border border-zinc-700 transition-all hover:scale-[1.02]">
                    <BookOpen className="w-7 h-7" />
                    <span className="text-sm font-medium">Academia</span>
                  </Button>
                </Link>
                <Link to="/ebooks">
                  <Button className="w-full h-auto py-5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-2xl flex flex-col items-center gap-2 border border-zinc-700 transition-all hover:scale-[1.02]">
                    <BookOpen className="w-7 h-7" />
                    <span className="text-sm font-medium">E-Books</span>
                  </Button>
                </Link>
                <Link to="/challenges">
                  <Button className="w-full h-auto py-5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-2xl flex flex-col items-center gap-2 border border-zinc-700 transition-all hover:scale-[1.02]">
                    <Trophy className="w-7 h-7" />
                    <span className="text-sm font-medium">Challenges</span>
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Columna Lateral */}
          <div className="space-y-6">

            {/* Recursos */}
            {user?.subscription_level !== 'free' && (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-3xl p-6">
                <h2 className="text-xl font-barlow font-bold text-white mb-5">Recursos</h2>
                <div className="space-y-3">
                  {/* Botón para ver Ebooks */}
                  <Link 
                    to="/ebooks"
                    className="p-4 bg-zinc-800/50 border border-zinc-700/50 rounded-xl flex items-center gap-4 hover:border-red-500/50 hover:bg-zinc-800 transition-all group"
                  >
                    <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                      <FileText className="w-6 h-6 text-red-500" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-white text-sm">Ebooks</p>
                      <p className="text-xs text-zinc-500">
                        {resources.ebooks.length > 0 
                          ? `${resources.ebooks.length} disponibles para tu nivel`
                          : 'Ver biblioteca de ebooks'}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-red-500 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  
                  {/* Botón para ver Conversatorios/Mindset */}
                  <Link 
                    to="/mindset"
                    className="p-4 bg-zinc-800/50 border border-zinc-700/50 rounded-xl flex items-center gap-4 hover:border-purple-500/50 hover:bg-zinc-800 transition-all group"
                  >
                    <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-purple-500" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-white text-sm">Conversatorios</p>
                      <p className="text-xs text-zinc-500">
                        {resources.conversatorios.length > 0 
                          ? `${resources.conversatorios.length} disponibles`
                          : 'Ver contenido de mindset'}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-purple-500 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            )}

            {/* Club VIP - Solo para usuarios Premium */}
            {hasClubVipAccess && premiumData && (premiumData.telegram_link || premiumData.whatsapp_link || premiumData.extra_materials?.length > 0) && (
              <div className="bg-gradient-to-br from-yellow-500/10 to-amber-500/10 border border-yellow-500/30 rounded-3xl p-6">
                <h2 className="text-xl font-barlow font-bold text-white mb-5 flex items-center gap-2">
                  <Crown className="w-5 h-5 text-yellow-500" />
                  Club VIP Premium
                </h2>
                <div className="space-y-3">
                  {/* Sección de Comunidad */}
                  {(premiumData.telegram_link || premiumData.whatsapp_link) && (
                    <div className="space-y-2">
                      <p className="text-xs text-yellow-400 font-medium mb-2 flex items-center gap-1">
                        <MessageCircle className="w-3 h-3" />
                        COMUNIDAD
                      </p>
                      
                      {/* Botón de Telegram */}
                      {premiumData.telegram_link && (
                        <a 
                          href={premiumData.telegram_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-4 bg-blue-500/20 border border-blue-500/30 rounded-xl flex items-center gap-4 hover:bg-blue-500/30 hover:border-blue-500/50 transition-all group"
                        >
                          <div className="w-12 h-12 bg-blue-500/30 rounded-xl flex items-center justify-center">
                            <MessageCircle className="w-6 h-6 text-blue-400" />
                          </div>
                          <div className="flex-1">
                            <p className="font-bold text-white text-sm">Comunidad Telegram</p>
                            <p className="text-xs text-blue-300">Únete a nuestra comunidad privada</p>
                          </div>
                          <ExternalLink className="w-5 h-5 text-blue-400 group-hover:translate-x-1 transition-transform" />
                        </a>
                      )}

                      {/* Botón de WhatsApp */}
                      {premiumData.whatsapp_link && (
                        <a 
                          href={premiumData.whatsapp_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-4 bg-green-500/20 border border-green-500/30 rounded-xl flex items-center gap-4 hover:bg-green-500/30 hover:border-green-500/50 transition-all group"
                        >
                          <div className="w-12 h-12 bg-green-500/30 rounded-xl flex items-center justify-center">
                            <MessageCircle className="w-6 h-6 text-green-400" />
                          </div>
                          <div className="flex-1">
                            <p className="font-bold text-white text-sm">Comunidad WhatsApp</p>
                            <p className="text-xs text-green-300">Únete a nuestro grupo de WhatsApp</p>
                          </div>
                          <ExternalLink className="w-5 h-5 text-green-400 group-hover:translate-x-1 transition-transform" />
                        </a>
                      )}
                    </div>
                  )}

                  {/* Material Extra */}
                  {premiumData.extra_materials && premiumData.extra_materials.length > 0 && (
                    <>
                      <div className="pt-2">
                        <p className="text-xs text-yellow-400 font-medium mb-2 flex items-center gap-1">
                          <Download className="w-3 h-3" />
                          MATERIAL EXCLUSIVO
                        </p>
                      </div>
                      {premiumData.extra_materials.map((material, idx) => (
                        <a 
                          key={idx}
                          href={material.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-4 bg-zinc-800/50 border border-zinc-700/50 rounded-xl flex items-center gap-4 hover:border-yellow-500/50 hover:bg-zinc-800 transition-all group"
                        >
                          <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                            <FileText className="w-6 h-6 text-green-500" />
                          </div>
                          <div className="flex-1">
                            <p className="font-bold text-white text-sm">{material.title}</p>
                            {material.description && (
                              <p className="text-xs text-zinc-500 whitespace-pre-line">{material.description}</p>
                            )}
                          </div>
                          <ExternalLink className="w-5 h-5 text-green-500 group-hover:translate-x-1 transition-transform" />
                        </a>
                      ))}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Comunidades WhatsApp por programa comprado */}
            {programCommunityLinks.length > 0 && (
              <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-3xl p-6" data-testid="programs-whatsapp-section">
                <h2 className="text-xl font-barlow font-bold text-white mb-5 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-green-400" />
                  Comunidad de tus programas
                </h2>
                <div className="space-y-3">
                  {programCommunityLinks
                    .map(({ key, prog, kind, href }) => (
                      <a
                        key={key}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-4 bg-green-500/20 border border-green-500/30 rounded-xl flex items-center gap-4 hover:bg-green-500/30 hover:border-green-500/50 transition-all group"
                        data-testid={`program-${kind}-${prog.program_id || prog.id}`}
                      >
                        <div className="w-12 h-12 bg-green-500/30 rounded-xl flex items-center justify-center">
                          <MessageCircle className="w-6 h-6 text-green-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-white text-sm truncate">{prog.name || prog.title || 'Programa'}</p>
                          <p className="text-xs text-green-300">{kind === 'telegram' ? 'Únete al grupo de Telegram del programa' : 'Únete al grupo de WhatsApp del programa'}</p>
                        </div>
                        <ExternalLink className="w-5 h-5 text-green-400 group-hover:translate-x-1 transition-transform" />
                      </a>
                    ))
                  }
                </div>
              </div>
            )}

            {/* Material Extra por programa comprado */}
            {programExtraMaterials.length > 0 && (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-3xl p-6" data-testid="programs-extra-materials-section">
                <h2 className="text-xl font-barlow font-bold text-white mb-5 flex items-center gap-2">
                  <Download className="w-5 h-5 text-yellow-400" />
                  Material Extra de tus programas
                </h2>
                <div className="space-y-3">
                  {programExtraMaterials.map(({ key, prog, material }) => (
                    <a
                      key={key}
                      href={material.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-4 bg-zinc-800/50 border border-zinc-700/50 rounded-xl flex items-center gap-4 hover:border-yellow-500/50 hover:bg-zinc-800 transition-all group"
                      data-testid={`program-extra-${prog.program_id || prog.id}`}
                    >
                      <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                        <FileText className="w-6 h-6 text-green-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-white text-sm">{material.title}</p>
                        <p className="text-xs text-zinc-500">{prog.name || prog.title || 'Programa'}</p>
                        {material.description && (
                          <p className="text-xs text-zinc-500 whitespace-pre-line">{material.description}</p>
                        )}
                      </div>
                      <ExternalLink className="w-5 h-5 text-green-500 group-hover:translate-x-1 transition-transform" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Mis Challenges - Solo si el usuario tiene challenges comprados */}
            {user?.purchased_challenges && user.purchased_challenges.length > 0 && (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-3xl p-6">
                <h2 className="text-xl font-barlow font-bold text-white mb-5 flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-pink-500" />
                  Mis Challenges
                </h2>
                <div className="space-y-3">
                  {resources.retos
                    .filter(r => user.purchased_challenges.includes(r.challenge_id))
                    .map((challenge) => (
                      <Link 
                        key={challenge.challenge_id}
                        to={`/challenge/${challenge.challenge_id}`}
                        className="p-4 bg-zinc-800/50 border border-zinc-700/50 rounded-xl flex items-center gap-4 hover:border-pink-500/50 hover:bg-zinc-800 transition-all group"
                      >
                        {challenge.image_url ? (
                          <img 
                            src={challenge.image_url.startsWith('http') ? challenge.image_url : `${BACKEND_URL}${challenge.image_url}`}
                            alt={challenge.title}
                            className="w-12 h-12 rounded-xl object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-pink-500/20 rounded-xl flex items-center justify-center">
                            <Trophy className="w-6 h-6 text-pink-500" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-white text-sm truncate">{challenge.title || 'Challenge'}</p>
                          <p className="text-xs text-green-400 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Inscrita - Ver contenido
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-pink-500 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    ))
                  }
                  
                  {user.purchased_challenges.length > 0 && 
                   resources.retos.filter(r => user.purchased_challenges.includes(r.challenge_id)).length === 0 && (
                    <p className="text-sm text-zinc-500 text-center py-4">
                      Cargando tus challenges...
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Admin link */}
            {user?.role === 'admin' && (
              <Link to="/admin">
                <div className="bg-gradient-to-r from-pink-600/20 to-pink-500/10 border border-pink-500/30 rounded-3xl p-6 hover:border-pink-500/50 transition-all group">
                  <h3 className="font-bold text-white mb-1 flex items-center gap-2">
                    Panel de Administración
                    <ChevronRight className="w-4 h-4 text-pink-500 group-hover:translate-x-1 transition-transform" />
                  </h3>
                  <p className="text-sm text-zinc-400">Gestionar contenido y usuarios</p>
                </div>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
